import parseRenderingContext from '@utils/parse-rendering-context';
import AjaxLoader from '../ajax-loader/ajax-loader';
import CartModal from './cart-modal';
import template from './templates/cart-summary';
import { defaults } from './cart-mapping';
import store from '@stores/cart/cart-store';

const AJAX_LOADER_OPTIONS = {
    aria: false,
    size: 'xs',
    visible: true
};

export default class CartSummary {
    constructor($originEl, context) {
        context = { ...defaults.context, ...context };

        const $parsed = this.parseTemplate();
        $originEl.replaceWith($parsed);

        this.$el = $parsed;
        this.$originEl = $originEl;

        this.$checkoutLabel = this.$el.find('[data-label="checkout"]');
        this.$continueShoppingLabel = this.$el.find('[data-label="continueShopping"]');
        this.$discountsLabel = this.$el.find('[data-label="discounts"]');
        this.$orderSummaryLabel = this.$el.find('[data-label="orderSummary"]');
        this.$promotionsLabel = this.$el.find('[data-label="promotions"]');
        this.$shippingLabel = this.$el.find('[data-label="shipping"]');
        this.$subtotalLabel = this.$el.find('[data-label="subtotal"]');
        this.$taxLabel = this.$el.find('[data-label="tax"]');
        this.$totalLabel = this.$el.find('[data-label="total"]');

        this.$continueShopping = this.$el.find('[data-region="continueShopping"]');
        this.$discounts = this.$el.find('[data-region="discounts"]');
        this.$error = this.$el.find('[data-region="error"]');
        this.$heading = this.$el.find('[data-region="heading"]');
        this.$loader = this.$el.find('[data-region="loader"]');
        this.$list = this.$el.find('[data-region="list"]');
        this.$shipping = this.$el.find('[data-region="shipping"]');
        this.$subtotal = this.$el.find('[data-region="subtotal"]');
        this.$tax = this.$el.find('[data-region="tax"]');
        this.$total = this.$el.find('[data-region="total"]');
        this.$upsell = this.$el.find('[data-region="upsell"]');
        this.$upsellContainer = this.$el.find('[data-region="upsellContainer"]');

        this.$checkoutCtrl = this.$el.find('[data-control="checkout"]');

        this.$checkoutLoaderPlaceholder = this.$el.find('[data-placeholder="loaderCheckout"]');
        this.$promotionsnote = this.$el.find('[data-region="promotionsnote"]');


        this.modal = null;

        this.checkoutLoader = null;
        this.loaders = [];

        this.aria = true;
        this.context = context;

        if (this.context.PostActionUrl) {
            store.mutations.SET_POST_ACTION_URL(this.context.PostActionUrl);
        }
    }

    init() {
        this.renderLabels();
        this.renderAttributes();
        this.renderContinueShoppingLink();
        this.renderLoadingState();
        this.setupAccessibility();
        this.setupListeners();
        this.setupModal();
        this.updateState(store.state.loading);
    }

    show() {
        this.$el.slideDown('fast', () => this.$el.removeAttr('style'));
    }

    hide() {
        this.$el.slideUp('fast');
    }

    async onCheckoutClick(evt) {
        evt.preventDefault();

        this.$error.hide();

        if (store.getters.hasPendingUpdate) {
            await this.modal.prompt();
        }
        if(store.getters.hasPendingPet || store.getters.isBackOrdered) {
            let errorMessagetext = '';

            if(store.getters.hasPendingPet){
                store.mutations.SET_PET_SELECTION_VALIDATED(true);
                errorMessagetext += '<div>' + this.context.PetSelectorError + '</div>';
            }
            if(store.getters.isBackOrdered){
                errorMessagetext +=  '<div>' + this.context.BackOrderError + '</div>';
            }
            this.$error.show().html(errorMessagetext);
            return;
        }

        const href = evt.currentTarget.href;
        if(href) {
            window.location.href = href;
        }
    }

    onLinesUpdate() {
        if(!store.getters.hasPendingPet && !store.getters.isBackOrdered) {
            store.mutations.SET_PET_SELECTION_VALIDATED(false);
            this.$error.hide();
        }else{
            let errorMessagetext = '';

            if(store.getters.hasPendingPet){
                store.mutations.SET_PET_SELECTION_VALIDATED(true);
                errorMessagetext += '<div>' + this.context.PetSelectorError + '</div>';
            }
            if(store.getters.isBackOrdered){
                errorMessagetext +=  '<div>' + this.context.BackOrderError + '</div>';
            }
            this.$error.show().html(errorMessagetext);
        }

        if(store.getters.isEmpty) {
            this.hide();
        } else {
            this.show();
        }
    }

    renderAttributes() {
        if(this.$checkoutCtrl.is('a')) {
            this.$checkoutCtrl.attr('href', this.context.PostActionUrl);
        }
    }

    renderContinueShoppingLink() {
        if(this.context.ContinueShoppingLink?.Url) {
            this.$continueShoppingLabel.html(this.context.ContinueShoppingLink.Text);
            if(this.$continueShopping.is('a')) {
                this.$continueShopping.attr('href', this.context.ContinueShoppingLink.Url);
            }
        } else {
            this.$continueShopping.hide();
        }
    }

    renderLabels() {
        this.$checkoutLabel.html(this.context.Checkout);
        this.$discountsLabel.html(this.context.Discounts);
        this.$orderSummaryLabel.html(this.context.OrderSummary);
        this.$promotionsLabel.html(this.context.Promotions);
        this.$shippingLabel.html(this.context.Shipping);
        this.$subtotalLabel.html(this.context.Subtotal);
        this.$taxLabel.html(this.context.Tax);
        this.$totalLabel.html(this.context.Total);

        if(this.context.PromotionNoteIcon && this.context.PromotionNote){
            let pnote = `<img src="${this.context.PromotionNoteIcon}" /><div>${this.context.PromotionNote}</div>`;
            this.$promotionsnote.html(pnote);
            this.$promotionsnote.addClass("mt-3");
        }else{
            this.$promotionsnote.hide();
        }
    }

    renderLoadingState() {
        this.checkoutLoader = new AjaxLoader(this.$checkoutLoaderPlaceholder, {
            aria: false,
            size: 'xs',
            srText: this.context.Loading
        });
        this.checkoutLoader.render();

        this.loaders =
            this.$loader
                .map(i => {
                    const $el = this.$loader.eq(i);
                    const describedbyId = $el.data('describedby');
                    const descriptionText = describedbyId ? $(describedbyId).text() : 'Cart Summary';
                    const loadingSrText = this.context.LoadingSummary?.replace('{0}', descriptionText);

                    return new AjaxLoader($el, {
                        ...AJAX_LOADER_OPTIONS,
                        srText: loadingSrText
                    });
                }).toArray();
    }

    setupAccessibility() {
        if (this.aria) {
            this.$el.attr('aria-live', 'polite');
        }
    }

    setupListeners() {
        this.$checkoutCtrl.on('click', (evt) => this.onCheckoutClick(evt));

        store.watch('lines', () => this.onLinesUpdate());
        store.watch('loading', (state) => (this.checkoutLoader.active = state.loading));
        store.watch('loadingLine', (state) => this.updateState(!!state.loadingLine));

        if(store.getters.isBackOrdered){
            this.$error.show().html(this.context.BackOrderError);
        }
        
    }

    setupModal() {
        this.modal = new CartModal({
            body: this.context.ModalCheckoutBody,
            buttonContinue: this.context.ModalCheckoutContinue,
            buttonUpdate: this.context.ModalCheckoutApplyUpdates,
            heading: this.context.ModalCheckoutHeading
        });
    }

    updateState(isLoading) {
        if (!isLoading) {
            this.hideLoading();
        } else {
            this.showLoading();
        }
    }

    updateUpsell() {
        if(store.state.freeShippingText) {
            this.$upsellContainer.show();
            this.$upsell.html(store.state.freeShippingText);
        } else {
            this.$upsellContainer.hide();
        }
    }

    hideLoading() {
        this.loaders.forEach(loader => (loader.active = false));
        this.$checkoutCtrl.removeClass('disabled').attr('aria-disabled', null);

        this.$discounts.html(store.getters.formattedDiscount);
        this.$shipping.html(store.getters.formattedShippingTotal);
        this.$subtotal.html(store.getters.formattedSubtotal);
        this.$tax.html(store.getters.formattedTaxTotal);
        this.$total.html(store.getters.formattedTotal);

        this.updateUpsell();
    }

    showLoading() {
        this.$discounts.empty();
        this.$shipping.empty();
        this.$subtotal.empty();
        this.$tax.empty();
        this.$total.empty();

        this.loaders.forEach(loader => (loader.active = true));
        this.$checkoutCtrl.addClass('disabled').attr('aria-disabled', true);
    }

    parseTemplate(templateStr = template) {
        const parsed = $.parseHTML(templateStr);
        return $(parsed);
    }

    static extendTemplate(templateStr) {
        class CartSummaryExtended extends this { };
        CartSummaryExtended.prototype.parseTemplate = CartSummary.prototype.parseTemplate.bind(CartSummaryExtended, templateStr);
        return CartSummaryExtended;
    }
}

$(() => {
    const el = document.querySelector('.js-cart-summary');
    if(el) {
        const $el = $(el);
        const renderingContext = parseRenderingContext(el);

        if(!store.state.loaded) {
            const unSubscribe = store.watch('loaded', (...[, , loaded]) => {
                if (loaded) {
                    if (!store.getters.isEmpty) {
                        const summary = new CartSummary($el, renderingContext);
    
                        summary.$el.css('display', 'none');
                        summary.init();
                        summary.show();
                    }
    
                    unSubscribe();
                }
            });
        }

        store.actions.getCart();
    };
});
